import React from 'react';

import classNames from 'classnames';

import { formatMoneyWithLocale } from 'Helpers/formatService';

/**
 * SponsorImpactHeader
 *
 * The headline of the Commit Sponsor screen — the sponsorship sibling of RevenueImpactHeader:
 *   Uncommitted now  →  This commitment  →  Uncommitted after
 *
 * Sponsorship is value-only (no quantity, no price) but always split cash / VIK: it is the
 * highest-VIK area of the budget, so unlike Direct Commitment — which hides the split when a line
 * carries no VIK — both components are shown unconditionally here. The combined figure leads
 * because that is what users reconcile on; the two components sit beneath it.
 *
 * Over-commitment is hard-blocked rather than warned about (IB-943 — matching ticketing, diverging
 * from Direct Commitment and Commit Revenue), so an "after" balance below zero is no longer
 * reachable through this screen. It is still rendered in red when one occurs, because a balance left
 * negative by an earlier commit or a release must stay visible rather than silent.
 *
 * Props:
 *  - context:    { crumbs, line }  what is being committed against
 *  - now:        { cash, vik }     uncommitted balance before this commitment
 *  - committing: { cash, vik }     what is being committed now
 *  - blocked:    boolean           the parent will not let this commitment through
 */
export default function SponsorImpactHeader({ context = {}, now = { cash: 0, vik: 0 }, committing = { cash: 0, vik: 0 }, blocked = false }) {
    const after = {
        cash: (now.cash ?? 0) - (committing.cash ?? 0),
        vik: (now.vik ?? 0) - (committing.vik ?? 0),
    };

    const Stat = ({ label, cash, vik, negative = false, positive = false, highlight = false }) => {
        const cashValue = cash ?? 0;
        const vikValue = vik ?? 0;
        const combined = cashValue + vikValue;
        const isNegative = negative && combined < 0;
        const isCashNegative = negative && cashValue < 0;
        const isVikNegative = negative && vikValue < 0;

        return (
            <div
                className={classNames('tw-px-5 tw-py-4 tw-min-w-0', {
                    'tw-bg-green-25/40': highlight && !isNegative,
                    'tw-bg-red-25/40': highlight && isNegative,
                    'tw-bg-charcoal-10': !highlight,
                })}
            >
                <div className="tw-text-xs tw-uppercase tw-tracking-wide tw-text-charcoal-50 tw-font-semibold tw-mb-1">{label}</div>
                <div className="tw-flex tw-flex-col tw-gap-y-0.5">
                    <div
                        className={classNames('tw-text-xl tw-font-bold tw-tabular-nums', {
                            'tw-text-red': isNegative,
                            'tw-text-green': positive && !isNegative,
                            'tw-text-charcoal': !isNegative && !positive,
                        })}
                    >
                        {formatMoneyWithLocale(combined)}
                    </div>
                    {/* Always rendered, both components: the cash / VIK split is mandatory on this
                        screen rather than conditional on the deal carrying VIK. */}
                    <div
                        className={classNames('tw-text-sm tw-font-semibold tw-tabular-nums', {
                            'tw-text-red': isCashNegative,
                            'tw-text-green': positive && !isCashNegative,
                            'tw-text-charcoal-75': !isCashNegative && !positive,
                        })}
                    >
                        {$t('Cash')}: {formatMoneyWithLocale(cashValue)}
                    </div>
                    <div
                        className={classNames('tw-text-sm tw-font-semibold tw-tabular-nums', {
                            'tw-text-red': isVikNegative,
                            'tw-text-green': positive && !isVikNegative,
                            'tw-text-charcoal-75': !isVikNegative && !positive,
                        })}
                    >
                        {$t('VIK')}: {formatMoneyWithLocale(vikValue)}
                    </div>
                </div>
            </div>
        );
    };

    return (
        <div
            className={classNames(
                // 2x2 below xl (a narrow viewport plus the sidebar cannot fit four tiles in a row),
                // 1x4 from xl up. Hairlines come from a 1px gap over the container background —
                // divide-x only draws column dividers, which breaks down in the 2x2 arrangement.
                'tw-grid tw-grid-cols-1 md:tw-grid-cols-2 xl:tw-grid-cols-4 tw-gap-px tw-border tw-rounded-lg tw-overflow-hidden tw-bg-charcoal-15',
                blocked ? 'tw-border-red' : 'tw-border-charcoal-15',
            )}
        >
            <div className="tw-px-5 tw-py-4 tw-min-w-0 tw-bg-charcoal-10">
                {context.crumbs && <div className="tw-text-xs tw-text-charcoal-50 tw-font-medium tw-mb-1">{context.crumbs}</div>}
                <div className="tw-text-lg tw-font-bold tw-text-charcoal">{context.line || '-'}</div>
            </div>
            <Stat label={$t('Uncommitted now')} cash={now.cash ?? 0} vik={now.vik ?? 0} />
            {/* Positive and green: sponsorship is income, so the amount being committed is money
                arriving. It used to be rendered as a red negative, borrowed from the expenditure
                screen, which read as a loss — and negating a zero VIK component printed "VIK: -0",
                because Intl.NumberFormat renders JavaScript's negative zero with its sign. Passing
                the committing figures unnegated removes both problems at once. */}
            <Stat label={$t('This commitment')} cash={committing.cash ?? 0} vik={committing.vik ?? 0} positive />
            <Stat label={$t('Uncommitted after')} cash={after.cash} vik={after.vik} negative highlight />
        </div>
    );
}
